import { useEffect, useMemo, useState } from 'react';
import {
  getAnnouncementCenter,
  markAllAnnouncementsAsRead,
  markAnnouncementAsRead,
  type AnnouncementCenterItem,
} from '@/api';
import { dispatchToast } from '@/components/ui/GlobalToast';

const TYPE_CONFIG = {
  info: { color: '#0ea5e9', bg: 'bg-[#0ea5e9]/10', border: 'border-[#0ea5e9]/20', icon: 'ri-information-line' },
  warning: { color: '#f59e0b', bg: 'bg-amber-50', border: 'border-amber-200', icon: 'ri-alarm-warning-line' },
  success: { color: '#10b981', bg: 'bg-[#10b981]/10', border: 'border-[#10b981]/20', icon: 'ri-sparkling-line' },
  event: { color: '#f97316', bg: 'bg-[#f97316]/10', border: 'border-[#f97316]/20', icon: 'ri-gift-line' },
} as const;

function formatDate(value: string) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export default function AnnouncementCenter() {
  const [items, setItems] = useState<AnnouncementCenterItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingIds, setSavingIds] = useState<string[]>([]);

  const unreadCount = useMemo(() => items.filter((item) => !item.read).length, [items]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getAnnouncementCenter();
        if (!active) return;
        setItems(Array.isArray(res) ? res : []);
      } catch (err) {
        if (!active) return;
        setItems([]);
        setError(err instanceof Error ? err.message : '公告加载失败');
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, []);

  const toggleRead = async (item: AnnouncementCenterItem) => {
    if (item.read || savingIds.includes(item.id)) return;
    setSavingIds((curr) => [...curr, item.id]);
    try {
      await markAnnouncementAsRead(item.id);
      setItems((curr) => curr.map((row) => (row.id === item.id ? { ...row, read: true } : row)));
      dispatchToast('success', '公告已标记为已读');
    } catch (err) {
      dispatchToast('error', err instanceof Error ? err.message : '标记已读失败');
    } finally {
      setSavingIds((curr) => curr.filter((id) => id !== item.id));
    }
  };

  const markAll = async () => {
    const unreadIds = items.filter((item) => !item.read).map((item) => item.id);
    if (unreadIds.length === 0) return;
    setSavingIds((curr) => Array.from(new Set([...curr, ...unreadIds])));
    try {
      await markAllAnnouncementsAsRead(unreadIds);
      setItems((curr) => curr.map((item) => ({ ...item, read: true })));
      dispatchToast('success', '全部公告已标记为已读');
    } catch (err) {
      dispatchToast('error', err instanceof Error ? err.message : '批量标记失败');
    } finally {
      setSavingIds((curr) => curr.filter((id) => !unreadIds.includes(id)));
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-bold text-gray-900 text-lg">站内公告</h2>
            <p className="text-gray-500 text-sm mt-1">这里会保留当前仍然有效的公告，登录后可随时查看。</p>
          </div>
          <button
            onClick={() => void markAll()}
            disabled={loading || unreadCount === 0}
            className="px-4 py-2 bg-[#0ea5e9] text-white text-sm font-medium rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            全部已读
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          {[
            { label: '公告总数', value: items.length, color: '#0ea5e9' },
            { label: '未读公告', value: unreadCount, color: '#f97316' },
            { label: '已读公告', value: items.length - unreadCount, color: '#10b981' },
          ].map((stat) => (
            <div key={stat.label} className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
              <p className="text-xs text-gray-400">{stat.label}</p>
              <p className="text-2xl font-bold mt-1" style={{ color: stat.color }}>{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-500">正在加载公告...</div>
      ) : error ? (
        <div className="bg-white rounded-2xl border border-red-100 p-8 text-center text-red-500">公告加载失败：{error}</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-2xl mx-auto mb-4">
            <i className="ri-megaphone-line text-3xl text-gray-300"></i>
          </div>
          <p className="font-medium text-gray-500 mb-1">当前没有可展示的公告</p>
          <p className="text-sm text-gray-400">管理员发布有效公告后会在这里显示。</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.info;
            return (
              <div
                key={item.id}
                className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-gray-200 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${cfg.bg}`}>
                    <i className={`${cfg.icon} text-lg`} style={{ color: cfg.color }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-gray-900 text-base truncate">{item.title}</h3>
                          <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                            {item.type === 'info' ? '普通公告' : item.type === 'warning' ? '重要提醒' : item.type === 'success' ? '活动通知' : '活动公告'}
                          </span>
                          {item.read ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">已读</span>
                          ) : (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#f97316]/10 text-[#f97316] font-medium">未读</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          发布于 {formatDate(item.publishedAt)} · {item.createdBy || '管理员'}
                        </p>
                      </div>
                      {!item.read && (
                        <button
                          onClick={() => void toggleRead(item)}
                          disabled={savingIds.includes(item.id)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#0ea5e9]/10 text-[#0ea5e9] hover:bg-[#0ea5e9]/20 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingIds.includes(item.id) ? '处理中...' : '标为已读'}
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mt-3 whitespace-pre-wrap">{item.content}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
